import { IconCheck, IconSearch } from '@tabler/icons-react'
import { Box, Button, Group, Modal, Text, TextInput, CloseButton } from 'components'
import { ISkillsStandards } from 'pages/NewProject/components/ProjectSkillsStandards/interfaces'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SelectedSkillStandard } from '../../../../types'
import { MIN_SEARCH_CHARS, useCurriculumSearch } from '../CurriculumTreeSearch'
import { BrowseTreeBreadcrumb, BrowseTreeList, BrowseTreeProvider, BrowseTreeTabs } from './components/BrowseTree'
import { SearchFilters } from './SearchFilters'
import { SearchResultsList } from './SearchResultsList'
import { transformToSelectedItems } from './transformers'
import { useModalState } from './useModalState'

interface AddSkillsModalProps {
  opened: boolean
  onClose: () => void
  selectedItems: SelectedSkillStandard[]
  onAddItems: (items: SelectedSkillStandard[]) => void
  skillsStandards: ISkillsStandards | null
  isLoading?: boolean
}

export const AddSkillsModal = ({
  opened,
  onClose,
  selectedItems,
  onAddItems,
  skillsStandards,
  isLoading = false,
}: AddSkillsModalProps) => {
  const { t } = useTranslation()
  const isTreeEnabled = true

  const { searchTerm, setSearchTerm, debouncedTerm, results, isSearching } = useCurriculumSearch(skillsStandards, {
    minChars: MIN_SEARCH_CHARS,
  })

  const {
    pendingItems,
    setPendingItems,
    filterType,
    handleFilterTypeChange,
    filterCategories,
    setFilterCategories,
    filterGrades,
    setFilterGrades,
    showResults,
    alreadyAddedIds,
    pendingIds,
    availableCategories,
    availableGrades,
    filteredResults,
    handleToggleItem,
    reset,
    clearFilters,
    hasFilters,
  } = useModalState(selectedItems, results, isSearching, skillsStandards)

  const searchInputRef = useRef<HTMLInputElement>(null)

  const isSearchMode = debouncedTerm.length >= MIN_SEARCH_CHARS
  const showBrowseTree = isTreeEnabled && !isSearchMode
  const showSearchError = searchTerm.length > 0 && searchTerm.length < MIN_SEARCH_CHARS

  useEffect(() => {
    if (opened) {
      // Focus after modal animation
      setTimeout(() => searchInputRef.current?.focus(), 100)
    } else {
      setSearchTerm('')
      reset()
    }
  }, [opened, setSearchTerm, reset])

  const handleClose = useCallback(() => {
    setSearchTerm('')
    reset()
    onClose()
  }, [setSearchTerm, reset, onClose])

  const [isAdding, setIsAdding] = useState(false)

  const handleAddSelected = useCallback(async () => {
    if (pendingItems.length === 0 || isAdding) return
    setIsAdding(true)
    try {
      await onAddItems(transformToSelectedItems(pendingItems))
      setPendingItems([])
      onClose()
    } catch (error: unknown) {
      console.error('Failed to add items:', error)
    } finally {
      setIsAdding(false)
    }
  }, [pendingItems, onAddItems, onClose, setPendingItems, isAdding])

  const showFilters = results.length > 0 && (availableCategories.length > 0 || availableGrades.length > 0)

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('arc_add_standards_skills')}
      centered
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      size='80%'
      styles={theme => ({
        inner: {
          padding: 24,
        },
        modal: {
          width: '80vw',
          maxWidth: 1000,
          height: '80vh',
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex',
          flexDirection: 'column' as const,
          overflow: 'hidden',
          borderRadius: 16,
        },
        content: {
          width: '80vw',
          maxWidth: 1000,
          height: '80vh',
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex',
          flexDirection: 'column' as const,
          overflow: 'hidden',
          borderRadius: 16,
        },
        header: {
          padding: '20px 24px',
          marginBottom: 0,
          flexShrink: 0,
          borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
        },
        title: {
          fontSize: 20,
          fontWeight: 600,
          letterSpacing: -0.3,
        },
        body: {
          padding: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column' as const,
          overflow: 'hidden',
        },
      })}
    >
      {/* Fixed height container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Search - Fixed */}
        <Box px={24} pt={16} pb={12} sx={{ flexShrink: 0 }}>
          <TextInput
            ref={searchInputRef}
            placeholder={t('arc_browse_search_placeholder')}
            icon={<IconSearch size={18} stroke={1.5} color='#868e96' />}
            value={searchTerm}
            onChange={e => setSearchTerm(e.currentTarget.value)}
            size='md'
            radius='xl'
            error={showSearchError ? t('arc_search_min_chars_error') : undefined}
            rightSection={
              searchTerm.length > 0 ? (
                <CloseButton
                  size='sm'
                  radius='xl'
                  onClick={() => setSearchTerm('')}
                  aria-label={t('general_buttons_clear')}
                />
              ) : null
            }
            styles={theme => ({
              input: {
                backgroundColor: theme.white,
                border: showSearchError ? `1px solid ${theme.colors.red[5]}` : `1px solid ${theme.colors.gray[3]}`,
                fontSize: 14,
                height: 42,
                paddingLeft: 40,
                '&::placeholder': {
                  color: theme.colors.gray[5],
                },
                '&:focus': {
                  borderColor: showSearchError ? theme.colors.red[5] : theme.colors.teal[5],
                  boxShadow: 'none',
                },
              },
              icon: {
                paddingLeft: 14,
              },
            })}
          />
        </Box>

        {/* Content Area - Scrollable, takes remaining space */}
        {showBrowseTree ? (
          /* Browse Tree - Navigation by hierarchy (requires arc_tree feature flag) */
          <BrowseTreeProvider
            skillsStandards={skillsStandards}
            pendingIds={pendingIds}
            alreadyAddedIds={alreadyAddedIds}
            onToggle={handleToggleItem}
          >
            {/* Sticky Header - Tabs + Breadcrumb with solid background */}
            <Box
              sx={theme => ({
                flexShrink: 0,
                position: 'relative',
                zIndex: 10,
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              })}
            >
              <Box px={24}>
                <BrowseTreeTabs />
              </Box>
              <Box px={24} py={16}>
                <BrowseTreeBreadcrumb />
              </Box>
            </Box>

            {/* Tree List - Scrollable */}
            <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, height: 0 }}>
              <BrowseTreeList />
            </Box>
          </BrowseTreeProvider>
        ) : (
          <>
            {/* Filters - Fixed (only in search mode) */}
            {showFilters && (
              <Box px={24} pb={8} sx={{ flexShrink: 0 }}>
                <SearchFilters
                  filterType={filterType}
                  filterCategories={filterCategories}
                  filterGrades={filterGrades}
                  availableCategories={availableCategories}
                  availableGrades={availableGrades}
                  onFilterTypeChange={handleFilterTypeChange}
                  onFilterCategoriesChange={setFilterCategories}
                  onFilterGradesChange={setFilterGrades}
                  onClearFilters={clearFilters}
                  hasFilters={hasFilters}
                />
              </Box>
            )}

            {/* Search Results */}
            <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, height: 0 }}>
              <SearchResultsList
                results={filteredResults}
                totalResults={results.length}
                isSearching={isSearching}
                showResults={showResults}
                searchTermLength={debouncedTerm.length}
                pendingIds={pendingIds}
                alreadyAddedIds={alreadyAddedIds}
                hasFilters={hasFilters}
                onToggleItem={handleToggleItem}
              />
            </Box>
          </>
        )}

        {/* Footer - Fixed at bottom */}
        <Box
          px={24}
          py={16}
          sx={theme => ({
            flexShrink: 0,
            borderTop: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
          })}
        >
          <Group position='apart' align='center'>
            {pendingItems.length > 0 ? (
              <Group spacing={8}>
                <IconCheck size={18} color='#20c997' stroke={2.5} />
                <Text size='sm' color='teal' weight={500} sx={{ fontSize: 14 }}>
                  {t('arc_items_pending_count', { count: pendingItems.length })}
                </Text>
              </Group>
            ) : (
              <Box />
            )}
            <Group spacing={12}>
              <Button
                variant='outline'
                color='gray'
                onClick={handleClose}
                radius='xl'
                sx={theme => ({
                  borderColor: theme.colors.gray[4],
                  color: theme.black,
                  fontWeight: 500,
                  height: 40,
                  paddingLeft: 20,
                  paddingRight: 20,
                  '&:hover': {
                    backgroundColor: theme.colors.gray[0],
                  },
                })}
              >
                {t('general_buttons_cancel')}
              </Button>
              <Button
                color='teal'
                onClick={handleAddSelected}
                disabled={pendingItems.length === 0 || isAdding}
                loading={isAdding || isLoading}
                radius='xl'
                sx={{
                  fontWeight: 500,
                  height: 40,
                  paddingLeft: 24,
                  paddingRight: 24,
                }}
              >
                {t('arc_add_selected', { count: pendingItems.length })}
              </Button>
            </Group>
          </Group>
        </Box>
      </Box>
    </Modal>
  )
}
